/**
 * Utility functions for carbon emissions calculations
 */
import { 
  Config,
  OperatingScenario,
  Params
} from '../types/config';
import { CarbonEmissionsResults } from '../types/calculations';

/**
 * Calculate carbon emissions for a given operating scenario
 * 
 * @param config - System configuration
 * @param params - System parameters
 * @param scenario - Operating scenario (current or expanded)
 * @returns Carbon emissions results
 */
export const calculateCarbonEmissions = (
  config: Config,
  _params: Params, // Using underscore to indicate it's unused in the typescript sense
  scenario: OperatingScenario
): CarbonEmissionsResults => {
  const currentConfig = config[scenario];
  
  // Emission factors in kg CO2e/kWh
  const gridEmissionFactor = 0.5; // UAE grid average
  const generatorEmissionFactor = 0.8; // Diesel generator
  const solarEmissionFactor = 0.04; // Solar PV lifecycle emissions
  
  // We're using params in these calculations below
  
  // Carbon credit offset cost in AED per metric tonne of CO2
  const carbonCreditCost = 31.2; // AED per metric tonne of CO2
  
  // Annual energy in MWh
  const annualEnergy = currentConfig.dailyConsumption * 365;
  
  // Calculate emissions for each option (kg CO2e)
  const gridOnlyEmissions = annualEnergy * 1000 * gridEmissionFactor;
  
  const gridTiedEmissions = (currentConfig.gridNeeded * 365) * 1000 * gridEmissionFactor +
                          (currentConfig.directSolarUsage * 365) * 1000 * solarEmissionFactor;
  
  const batteryEmissions = (currentConfig.gridNeededWithBattery * 365) * 1000 * gridEmissionFactor +
                          (currentConfig.directSolarUsage * 365) * 1000 * solarEmissionFactor;
  
  const generatorEmissions = annualEnergy * 1000 * generatorEmissionFactor;
  
  // Calculate carbon offset costs (AED)
  const gridOnlyOffsetCost = (gridOnlyEmissions / 1000) * carbonCreditCost;
  const gridTiedOffsetCost = (gridTiedEmissions / 1000) * carbonCreditCost;
  const batteryOffsetCost = (batteryEmissions / 1000) * carbonCreditCost;
  const generatorOffsetCost = (generatorEmissions / 1000) * carbonCreditCost;
  
  // Calculate 25-year carbon offset costs (AED)
  const gridOnlyOffsetCost25Year = gridOnlyOffsetCost * 25;
  const gridTiedOffsetCost25Year = gridTiedOffsetCost * 25;
  const batteryOffsetCost25Year = batteryOffsetCost * 25;
  const generatorOffsetCost25Year = generatorOffsetCost * 25;
  
  // Calculate offset cost savings (AED)
  const batteryVsGridOffsetSavings = gridOnlyOffsetCost - batteryOffsetCost;
  const batteryVsGridOffsetSavings25Year = gridOnlyOffsetCost25Year - batteryOffsetCost25Year;
  
  return {
    // Emission values in kg CO2e
    gridOnly: gridOnlyEmissions,
    gridTied: gridTiedEmissions,
    battery: batteryEmissions,
    generator: generatorEmissions,
    
    // 25-year emissions in kg CO2e
    gridOnly25Year: gridOnlyEmissions * 25,
    gridTied25Year: gridTiedEmissions * 25,
    battery25Year: batteryEmissions * 25,
    generator25Year: generatorEmissions * 25,
    
    // Annual emission savings in kg CO2e
    batteryVsGridSavings: gridOnlyEmissions - batteryEmissions,
    batteryVsGridSavings25Year: (gridOnlyEmissions - batteryEmissions) * 25,
    
    // Carbon offset costs in AED
    gridOnlyOffsetCost,
    gridTiedOffsetCost,
    batteryOffsetCost,
    generatorOffsetCost,
    
    // 25-year carbon offset costs in AED
    gridOnlyOffsetCost25Year,
    gridTiedOffsetCost25Year,
    batteryOffsetCost25Year,
    generatorOffsetCost25Year,
    
    // Carbon offset cost savings in AED
    batteryVsGridOffsetSavings,
    batteryVsGridOffsetSavings25Year,
    
    // Reference values
    carbonCreditCost,
    gridEmissionFactor
  };
};

/**
 * Calculate environmental equivalents of carbon savings
 * 
 * @param carbonSavings - Carbon savings in kg CO2e
 * @returns Object with equivalent metrics 
 */
export const calculateEnvironmentalEquivalents = (carbonSavings: number) => {
  // Convert kg to metric tonnes
  const savingsTonnes = carbonSavings / 1000;
  
  return {
    // Average passenger vehicle driven for one year
    vehiclesPerYear: Math.round(savingsTonnes / 2.3),
    
    // Home electricity use for one year
    homesElectricity: Math.round(savingsTonnes * 0.12),
    
    // Acres of forest preserved for one year
    forestAcres: Math.round(savingsTonnes * 16.5),
    
    // Barrels of oil consumed
    oilBarrels: Math.round(savingsTonnes * 2.33),
    
    // Tree seedlings grown for 10 years
    treeSeedlings: Math.round(savingsTonnes * 16.5)
  };
};